import React from "react";
import type { InjectedFormProps } from "redux-form";
import { Field, reduxForm } from "redux-form";

import {
  Box,
  Button,
  Divider,
  Grid,
  Loader,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import {
  ArrowRightIcon,
  LinkedInIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { RouterLink } from "@js/components/link";
import { Form } from "@js/forms/components/form";
import { PasswordField, TextField } from "@js/forms/fields";
import { required } from "@js/forms/validators";
export const LOGIN_FORM_ID = "logIn";

import { PROF_NET_ONBOARDING_PATHS } from "../../constants";

import { LoginMFAModal } from "./login-mfa-modal";
import { signInWithLinkedIn } from "./sign-in-with-linkedin";

type OwnProps = {
  isExtraAuthRequired: boolean;
};

export type LoginFormData = {
  password: string;
  code?: string;
  email?: string;
  connect?: boolean;
  is_backup_code?: boolean;
};

type Props = InjectedFormProps<LoginFormData, OwnProps> & OwnProps;

const EmailLoginFormComponent = ({
  submit,
  error,
  submitting,
  isExtraAuthRequired,
}: Props) => {
  return (
    <Form onSubmit={submitting ? null : submit}>
      <Grid container spacing={3} sx={{ justifyContent: "center" }}>
        <LoginMFAModal />
        {error && (
          <Grid
            size={{
              xs: 12,
            }}
            sx={{ textAlign: "start !important", color: "var(--negative-1)" }}
          >
            {error}
          </Grid>
        )}
        <Grid
          size={{
            xs: 12,
          }}
        >
          {isExtraAuthRequired ? (
            <Field
              id="connect"
              name="connect"
              type="hidden"
              component="input"
            />
          ) : (
            <Field
              data-lpignore="true"
              name="email"
              id="email-address"
              validate={[required]}
              variant="outlined"
              color="secondary"
              component={TextField}
              autoFocus
              label="Email address"
            />
          )}
        </Grid>
        <Grid
          size={{
            xs: 12,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column-reverse" }}>
            <Field
              //special data atribute to prevent LastPass from filling this field
              data-lpignore="true"
              id="password"
              name="password"
              validate={[required]}
              color="secondary"
              variant="outlined"
              component={PasswordField}
              label="Password"
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                position: "relative",
                zIndex: "2",
                marginBottom: "-26px",
              }}
            >
              <Typography
                component="link"
                to="/auth/forgot_password/"
                size="small"
                RouterLink={RouterLink}
              >
                Forgot password?
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid
          size={{
            xs: 12,
          }}
        >
          <Button
            variant="dark-violet"
            size="medium"
            shape="squared"
            fullWidth
            sx={{
              mt: 2,
              mb: 2,
              boxShadow: "0px 4px 12px 0px #5D197440 !important",
              svg: { color: "var(--white)" },
            }}
            endIcon={submitting ? <Loader size={20} /> : <ArrowRightIcon />}
            disabledType="no-change"
            type="submit"
          >
            Sign in
          </Button>
          {!isExtraAuthRequired && (
            <>
              <Stack
                direction="row"
                sx={{ alignItems: "center", gap: 1, mb: 2 }}
              >
                <Box sx={{ flex: 1 }}>
                  <Divider color="beige" />
                </Box>
                <Typography component="span" color="grey-2">
                  or
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <Divider color="beige" />
                </Box>
              </Stack>
              <Button
                variant="secondary"
                disabled={submitting}
                size="medium"
                shape="squared"
                fullWidth
                endIcon={<LinkedInIcon />}
                onClick={signInWithLinkedIn}
                disabledType="no-change"
              >
                Sign in with LinkedIn
              </Button>
            </>
          )}
        </Grid>
        <Grid
          size={{
            xs: 12,
          }}
        >
          <Typography
            component="p"
            color="secondary"
            size="small"
            sx={{ mt: 1, textAlign: "center" }}
          >
            New to Braintrust?{" "}
            <Typography
              component="link"
              to={PROF_NET_ONBOARDING_PATHS.CREATE_ACCOUNT_PAGE.path}
              size="small"
              RouterLink={RouterLink}
              color="dark-violet"
              fontWeight={500}
            >
              Create an account.
            </Typography>
          </Typography>
        </Grid>
      </Grid>
    </Form>
  );
};

export const EmailLoginForm = reduxForm<LoginFormData, OwnProps>({
  form: LOGIN_FORM_ID,
})(EmailLoginFormComponent);
